import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import {
  Close,
  Add,
  Remove,
  Delete
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { recommendedAddOns } from '../../data/servicesData';
import OrderSuccessModal from './OrderSuccessModal';
// import { otpService } from '../../services/otpService'; // Firebase OTP (for production)
// import { testOtpService as otpService } from '../../services/testOtpService'; // Test OTP (for development)
import { smsOtpService as otpService } from '../../services/smsOtpService'; // SMS OTP (real SMS)
import { whatsappService } from '../../services/whatsappService';
import { locationService } from '../../services/locationService';

const DELIVERY_CHARGE_LABEL = 'As on Actual';
const GST_RATE = 0.05;

const scaleQuantityTextForGuests = (value, baseGuests, targetGuests) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const basis = Number(baseGuests) || 1;
  const target = Number(targetGuests) || 0;
  let matchedUnit = false;
  const scaledParts = raw.split('+').map((part) => {
    const match = part.trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*(KG|GM|PCS?|PC)(.*)$/i);
    if (!match) return part.trim();

    matchedUnit = true;
    const amount = Number(match[1]) || 0;
    const unit = match[2].toUpperCase();
    const suffix = match[3].trim();
    const scaled = (amount / basis) * target;
    const formatted = unit === 'KG'
      ? Number(scaled.toFixed(2))
      : unit === 'GM'
        ? Number(scaled.toFixed(1))
        : Math.round(scaled);
    const normalizedUnit = unit === 'PC' ? 'PCS' : unit;

    return `${formatted}${normalizedUnit}${suffix ? ` ${suffix}` : ''}`;
  });

  return matchedUnit ? scaledParts.join(' + ') : '';
};

const CartModal = ({ open, onClose, onCheckout, bookingConfig, onBookingConfigChange, guestCount, onGuestCountChange, selectedMenu, userEditRef, lastUserEditAt }) => {
  const { items, updateQuantity, removeItem, getTotalPrice, addItem } = useCart();
  const navigate = useNavigate();
  
  // Use dynamic values from bookingConfig or fallback to defaults
  const [deliveryDate, setDeliveryDate] = useState(bookingConfig?.eventDate || '15th October 2024');
  const [deliveryTime, setDeliveryTime] = useState(bookingConfig?.eventTime || '6:00 PM - 10:30 PM');
  const [couponCode, setCouponCode] = useState('');
  const [location, setLocation] = useState(bookingConfig?.location || 'Gurugram');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Validation error states
  const [phoneError, setPhoneError] = useState('');
  const [pinCodeError, setPinCodeError] = useState('');
  const [jainSelectionError, setJainSelectionError] = useState('');
  
  // OTP verification states for inline display
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Update state when bookingConfig changes
  React.useEffect(() => {
    if (bookingConfig) {
      if (bookingConfig.eventDate) {
        setDeliveryDate(bookingConfig.eventDate);
      }
      if (bookingConfig.eventTime) {
        setDeliveryTime(bookingConfig.eventTime);
      }
      if (bookingConfig.location) {
        setLocation(bookingConfig.location);
      }
    }
  }, [bookingConfig]);

  const handleDeliveryDateChange = useCallback((nextDate) => {
    setDeliveryDate(nextDate);
    onBookingConfigChange?.({ eventDate: nextDate });
  }, [onBookingConfigChange]);

  const handleDeliveryTimeChange = useCallback((nextTime) => {
    setDeliveryTime(nextTime);
    onBookingConfigChange?.({ eventTime: nextTime });
  }, [onBookingConfigChange]);

  // OTP resend timer effect
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && showOTP) {
      setCanResend(true);
    }
  }, [resendTimer, showOTP]);

  const handleQuantityChange = useCallback((id, newQuantity) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    const previousQuantity = parseInt(item.quantity) || 0;
    const delta = newQuantity - previousQuantity;
    const shouldUpdateGuestCount = !item.isAddon
      && item.category !== 'breads'
      && item.category !== 'desserts';
    

    // Determine which guest type to update
    let guestType = 'veg';
    if (selectedMenu === 'jain' || (selectedMenu === 'packages' && bookingConfig?.menu === 'jain')) {
      guestType = 'jain';
    } else if (item.isNonVeg) {
      guestType = 'nonVeg';
    } else if (item.isJain) {
      guestType = 'jain';
    } else if (item.isPackage || item.isPackageItem || selectedMenu === 'veg') {
      guestType = 'veg';
    }

    let newGuestCount = Math.max(1, newQuantity);
    if (selectedMenu === 'customized' && guestType !== 'jain') {
      const currentGuestCount = Number(guestCount?.[guestType] || 0);
      newGuestCount = Math.max(0, currentGuestCount + delta);
    }
    

    // 1) Propagate only guest-driven menu items to parent booking counts.
    if (shouldUpdateGuestCount && onGuestCountChange) {
      onGuestCountChange(guestType, newGuestCount);
    }

    // 2) Update the cart line so UI reflects the change immediately
    if (newQuantity <= 0) {
      removeItem(id);
    } else {
      const unitPrice = item.calculatedPrice || item.price || item.basePrice || 0;
      const scalePackageQuantity = (qty, unit, basePax, pax) => {
        const amount = Number(qty);
        if (!Number.isFinite(amount) || !amount) return '';
        const scaled = basePax > 0 ? (amount / basePax) * pax : amount;
        if (String(unit).toLowerCase() === 'kg') return `${Number(scaled.toFixed(2))}kg`;
        if (String(unit).toLowerCase() === 'gm') return `${Number(scaled.toFixed(1))}gm`;
        return `${Math.round(scaled)}pc`;
      };
      const cleanPackageName = (value) => String(value || '')
        .replace(/\s*\(([^)]*(pc|pcs|kg|gm|g|PCS|KG|GM)[^)]*)\)\s*/gi, ' ')
        .trim();
      const scaleExistingPackageDetail = (value) => {
        const text = String(value || '');
        const match = text.match(/^(.*?)\s+-\s+(.+)$/);
        if (!match || previousQuantity <= 0) return value;

        const scaledQuantity = scaleQuantityTextForGuests(match[2], previousQuantity, newQuantity);
        return scaledQuantity ? `${match[1].trim()} - ${scaledQuantity}` : value;
      };
      const scaleRegularQuantityText = () => {
        if (item.isPackage || item.packageKey || item.packageIncludes || previousQuantity <= 0) {
          return item.calculatedQuantity;
        }

        if (item.unit === 'portion' && item.portionSize) {
          return scaleQuantityTextForGuests(item.portionSize, 1, newQuantity) || item.calculatedQuantity;
        }

        const source = item.calculatedQuantity || item.customizations?.quantity || item.portion_size;
        return scaleQuantityTextForGuests(source, previousQuantity, newQuantity) || item.calculatedQuantity;
      };
      const getUpdatedPackageQuantityDetails = () => {
        if (!(item.isPackage || item.packageKey || item.packageIncludes)) {
          return item.packageQuantityDetails;
        }

        if (!item.packageItems || !item.packageDetails) {
          if (!item.packageQuantityDetails || typeof item.packageQuantityDetails !== 'object') {
            return item.packageQuantityDetails;
          }

          return Object.entries(item.packageQuantityDetails).reduce((details, [category, values]) => {
            details[category] = Array.isArray(values)
              ? values.map(scaleExistingPackageDetail)
              : values;
            return details;
          }, {});
        }

        const basePax = Math.max(1, parseInt(item.packagePax || 20) || 20);
        const categoryMap = {
          starters: 'starters',
          main: 'mainCourse',
          maincourse: 'mainCourse',
          rice: 'rice',
          breads: 'breads',
          dessert: 'desserts',
          desserts: 'desserts'
        };

        return Object.entries(item.packageDetails).reduce((details, [category, selectedNames]) => {
          if (category === 'extras') {
            details[category] = selectedNames;
            return details;
          }

          const sourceKey = categoryMap[category] || category;
          const sourceItems = item.packageItems[sourceKey] || [];
          const selectedRows = (selectedNames || []).map((selectedName) => {
            const sourceItem = sourceItems.find((pkgItem) => cleanPackageName(pkgItem?.name) === cleanPackageName(selectedName));
            if (!sourceItem) return selectedName;

            const qtyText = scalePackageQuantity(sourceItem.quantity, sourceItem.unit || '', basePax, newQuantity);
            const extraText = sourceItem.extra
              ? ` + ${scalePackageQuantity(sourceItem.extra.quantity, sourceItem.extra.unit || 'pc', basePax, newQuantity)}`
              : '';
            return `${cleanPackageName(sourceItem.name)} - ${qtyText}${extraText}`;
          });

          details[category] = selectedRows;
          return details;
        }, {});
      };
      const updatedQuantityText = scaleRegularQuantityText();
      const updatedItem = {
        ...item,
        quantity: newQuantity,
        serves: newQuantity,
        calculatedPrice: unitPrice,
        calculatedQuantity: updatedQuantityText || item.calculatedQuantity,
        customizations: item.customizations
          ? {
              ...item.customizations,
              quantity: updatedQuantityText || item.customizations.quantity
            }
          : item.customizations,
        packageQuantityDetails: getUpdatedPackageQuantityDetails()
      };
      updateQuantity(id, newQuantity, updatedItem);
    }

    // Skip if no guest count handler or no actual change
    if (!shouldUpdateGuestCount || !guestCount || !onGuestCountChange || delta === 0) {
      return;
    }

    // We already propagated above; nothing more to do here
  }, [items, removeItem, updateQuantity, selectedMenu, bookingConfig?.menu, onGuestCountChange, guestCount]);

  const handleSendOTP = async () => {
    const cleanNumber = String(phoneNumber || '').replace(/\D/g, '');
    if (cleanNumber) {
      // Validate phone number (10 digits)
      if (cleanNumber.length !== 10) {
        setPhoneError('Please enter a valid 10-digit phone number');
        return;
      }
      
      try {
        const result = await otpService.sendOTP(cleanNumber);
        
        if (result.success) {
          setShowOTP(true);
          setResendTimer(30);
          setCanResend(false);
          setOtp(['', '', '', '', '', '']);
          setOtpError('');
          setPhoneError('');
          
          if (result.testMode && result.dynamicOTP) {
            setOtpMessage(`Verification code sent. Test code: ${result.dynamicOTP}`);
          } else {
            setOtpMessage('Verification code sent successfully.');
          }
        } else {
          setPhoneError(result.message || 'Failed to send OTP');
        }
      } catch (error) {
        console.error('OTP Send Error:', error);
        setPhoneError('Failed to send OTP. Please try again.');
      }
    } else {
      setPhoneError('Please enter phone number');
    }
  };

  const handleSubmitValidation = () => {
    let isValid = true;
    
    // Clear previous errors
    setPhoneError('');
    setPinCodeError('');

    // Check if phone number is filled
    const cleanPhone = String(phoneNumber || '').replace(/\D/g, '');
    if (!cleanPhone) {
      setPhoneError('Please enter your phone number');
      isValid = false;
    }
    // Check if phone number is valid (10 digits)
    else if (cleanPhone.length !== 10) {
      setPhoneError('Please enter a valid 10-digit phone number');
      isValid = false;
    }

    // Check if pin code is filled
    const cleanPin = String(couponCode || '').replace(/\D/g, '');
    if (!cleanPin) {
      setPinCodeError('Please enter your pin code');
      isValid = false;
    }
    // Check if pin code has minimum length (assuming 6 digits)
    else if (cleanPin.length !== 6) {
      setPinCodeError('Please enter a valid 6-digit pin code');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async () => {
    const isJainMenu = selectedMenu === 'jain' || bookingConfig?.menu === 'jain';
    const selectedJainItemCount = new Set(
      items.filter((item) => item.isJain).map((item) => item.id)
    ).size;

    if (isJainMenu && selectedJainItemCount < 5) {
      setJainSelectionError(`Please select at least 5 Jain items before submitting (${selectedJainItemCount}/5 selected).`);
      return;
    }

    setJainSelectionError('');
    const cleanPhone = String(phoneNumber || '').replace(/\D/g, '');
    // If OTP is shown, verify OTP instead of normal validation
    if (showOTP) {
      const otpCode = otp.join('');
      if (otpCode.length !== 6) {
        setOtpError('Please enter complete 6-digit OTP');
        return;
      }

      try {
        const result = await otpService.verifyOTP(cleanPhone, otpCode);
        if (result.success) {
          setShowOTP(false);
          setShowSuccess(true);
        } else {
          setOtpError(result.message || 'Invalid OTP. Please try again.');
        }
      } catch (error) {
        console.error('OTP Verification Error:', error);
        setOtpError('Failed to verify OTP. Please try again.');
      }
    } else {
      // Check if phone number is entered and OTP should be sent first
      if (cleanPhone && cleanPhone.length === 10) {
        setPhoneError('Please send OTP first and verify it before submitting');
        return;
      }
      
      // Normal validation for other fields
      if (handleSubmitValidation()) {
        setShowSuccess(true);
      }
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const clean = String(value || '').replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(clean);
    if (phoneError && clean) {
      setPhoneError('');
    }
  };

  // Clear pin code error when user starts typing
  const handlePinCodeChange = (e) => {
    const value = e.target.value;
    const clean = String(value || '').replace(/\D/g, '').slice(0, 6);
    setCouponCode(clean);
    if (pinCodeError && clean) {
      setPinCodeError('');
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Clear error when user starts typing
      if (otpError && value) {
        setOtpError('');
      }
      
      // Auto focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (!canResend) return;
    
    try {
      const cleanPhone = String(phoneNumber || '').replace(/\D/g, '');
      const result = await otpService.sendOTP(cleanPhone);
      if (result.success) {
        setResendTimer(30);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        
        if (result.testMode && result.dynamicOTP) {
          setOtpMessage(`New verification code sent. Test code: ${result.dynamicOTP}`);
        } else {
          setOtpMessage('New verification code sent successfully.');
        }
      } else {
        setOtpError('Failed to resend OTP. Please try again.');
        setOtpMessage('');
      }
    } catch (error) {
      setOtpError('Failed to resend OTP. Please try again.');
      setOtpMessage('');
    }
  };

  const buildOrderData = useCallback(() => {
    const itemsTotal = Number(getTotalPrice()) || 0;
    const gst = Math.round(itemsTotal * GST_RATE);
    const totalPrice = itemsTotal + gst;

    const normalizedItems = (items || []).map((item) => {
      const unitPrice = Number(item.calculatedPrice ?? item.price ?? item.basePrice ?? 0) || 0;
      const qty = Number(item.quantity) || 0;
      return {
        ...item,
        price: unitPrice,
        quantity: qty
      };
    });
    const normalizedMenuType = selectedMenu || bookingConfig?.menu;
    const maxCartQuantity = normalizedItems.reduce((max, item) => Math.max(max, Number(item.quantity) || 0), 0);
    const normalizedGuestCount = {
      veg: Math.max(
        (normalizedMenuType === 'veg' || normalizedMenuType === 'packages') ? 15 : 0,
        Number(guestCount?.veg ?? bookingConfig?.vegCount ?? 0) || 0,
        (normalizedMenuType === 'veg' || normalizedMenuType === 'packages') ? maxCartQuantity : 0
      ),
      nonVeg: Number(guestCount?.nonVeg ?? bookingConfig?.nonVegCount ?? 0) || 0,
      jain: Number(guestCount?.jain ?? bookingConfig?.jainCount ?? 0) || 0
    };

    return {
      items: normalizedItems,
      subTotal: itemsTotal,
      deliveryCharge: DELIVERY_CHARGE_LABEL,
      gstAmount: gst,
      totalPrice,
      deliveryDate,
      deliveryTime,
      location,
      phoneNumber,
      pinCode: couponCode,
      menuType: normalizedMenuType,
      menuPackage: bookingConfig?.menuPackage,
      menuPackageName: bookingConfig?.menuPackageName,
      mealType: bookingConfig?.mealType,
      guestCount: normalizedGuestCount
    };
  }, [bookingConfig, couponCode, deliveryDate, deliveryTime, getTotalPrice, guestCount, items, location, phoneNumber, selectedMenu]);

  const handleSuccessOkay = useCallback(async () => {
    if (!phoneNumber || String(phoneNumber).trim().length < 10) return;
    try {
      await whatsappService.sendOrderDetails(phoneNumber, buildOrderData());
    } catch (error) {
      console.error('Failed to send WhatsApp message:', error);
    }
  }, [buildOrderData, phoneNumber]);

  // Handle adding recommended add-ons to cart
  const handleAddAddonToCart = (addon) => {
    if (!addon) return;

    const addonId = `addon_${addon.id}`;

    const addonItem = {
      id: addonId,
      name: addon.name,
      price: addon.price,
      calculatedPrice: addon.price,
      quantity: 1,
      isAddon: true,
      isVeg: true
    };

    addItem(addonItem);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    onClose();
  };


  // Memoize per-item derived display values to avoid recomputation on each render
  const mappedItems = useMemo(() => {
    return (items || []).map((item) => {
      const isBreadOrDessert = item.category === 'breads' || item.category === 'desserts';
      const isPackageMenuItem = Boolean(item.isPackage || item.packageKey || item.packageIncludes);
      const displayServes = isPackageMenuItem
        ? (Number(item.serves) || Number(item.quantity) || 1)
        : (isBreadOrDessert ? item.serves : item.quantity);

      // Derive quantity text dynamically to stay in sync with serves changes
      const computeQuantityText = () => {
        if (item.isAddon) return item.quantity;
        if (isPackageMenuItem) {
          const packageSelectionDetails = item.packageQuantityDetails || item.packageDetails || item.customizations?.selectedItems;
          if (packageSelectionDetails && typeof packageSelectionDetails === 'object') {
            const packageItemCount = Object.entries(packageSelectionDetails)
              .filter(([category, values]) => category !== 'extras' && Array.isArray(values))
              .reduce((total, [, values]) => total + values.length, 0);
            if (packageItemCount > 0) return packageItemCount;
          }
          return item.customizations?.quantity || item.calculatedQuantity || `${displayServes} pax`;
        }
        if (typeof item.calculatedQuantity === 'string' && /PCS|PC|GM|KG/i.test(item.calculatedQuantity)) {
          return item.calculatedQuantity;
        }
        if (typeof item.customizations?.quantity === 'string' && /PCS|PC|GM|KG/i.test(item.customizations.quantity)) {
          return item.customizations.quantity;
        }
        // Prefer dynamic recompute for PCS/GM/KG using per-serve derivation
        const qtySource = (typeof item.portion_size === 'string' && /PCS?|PC|GM|KG/i.test(item.portion_size))
          ? item.portion_size
          : (typeof item.quantity === 'string' && /PCS?|PC|GM|KG/i.test(item.quantity))
            ? item.quantity
            : null;

        if (qtySource) {
          const basisServes = Number(item.calculatedFor) || Number(item.originalServes) || Number(item.serves) || 1;
          const scaledQuantity = scaleQuantityTextForGuests(qtySource, basisServes, Number(displayServes) || 0);
          if (scaledQuantity) return scaledQuantity;
        }

        if (item.portionSize && typeof item.portionSize === 'string') {
          const basisServes = item.unit === 'portion'
            ? 1
            : Number(item.originalServes) || Number(item.serves) || Number(displayServes) || 1;
          const scaledQuantity = scaleQuantityTextForGuests(item.portionSize, basisServes, Number(displayServes) || 0);
          if (scaledQuantity) return scaledQuantity;
        }
        if (item.customizations?.quantity) return item.customizations.quantity;
        return '1';
      };

      const quantityText = computeQuantityText();
      const unitPrice = item.calculatedPrice || item.price || item.basePrice || 0;
      const lineTotal = unitPrice * (Number(displayServes) || 0);

      return {
        ...item,
        _displayServes: displayServes,
        _quantityText: quantityText,
        _lineTotal: lineTotal
      };
    });
  }, [items, guestCount]);

  const renderDeleteAction = (item, marginTop) => (
    <Typography
      variant="caption"
      onClick={() => removeItem(item.id)}
      sx={{
        color: '#d32f2f',
        fontSize: '0.75rem',
        cursor: 'pointer',
        mt: marginTop,
        ml: 1.25,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        '&:hover': { textDecoration: 'underline' }
      }}
    >
      <Delete sx={{ fontSize: '0.85rem' }} />
      Delete
    </Typography>
  );

  // Render as modal with new design
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '100vh' },
          maxWidth: { xs: '100%', sm: '400px' },
          width: { xs: '100%', sm: '400px' },
          margin: { xs: 0, sm: 0 },
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          left: { xs: 0, sm: 'auto' }
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          justifyContent: 'flex-end',
          alignItems: 'stretch',
          margin: 0,
          maxWidth: 'none',
          height: '100vh'
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }
      }}
    >
      <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa', height: { xs: '100vh', sm: 'auto' }, maxHeight: { xs: '100vh', sm: '90vh' }, overflowY: 'auto' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Order Summary
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Cart Items - Single Box for all items */}
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            bgcolor: 'white',
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {/* Dynamic Cart Items */}
            {mappedItems.map((item, index) => {
              const displayServes = item._displayServes;
              const quantityText = item._quantityText;
              return (
              <Box key={item.id}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 2
                }}>
                  {/* Left Side - Veg Indicator and Item Details */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {/* Veg/Non-Veg Indicator */}
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          border: '2px solid',
                          borderColor: item.isVeg ? '#4caf50' : '#f44336',
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'white'
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            backgroundColor: item.isVeg ? '#4caf50' : '#f44336',
                            borderRadius: '50%'
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {item.name}
                      </Typography>
                    </Box>
                    
                    {/* Package Details or Regular Item Details */}
                    {item.isPackage ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                          {item.packageType === 'premium' ? 'Premium' : 'Standard'} Package - Serves: {item.serves} | Select Items: {quantityText}
                        </Typography>
                        {(item.packageQuantityDetails || item.packageDetails) && (
                          <Box sx={{ ml: 1 }}>
                            {Object.entries(item.packageQuantityDetails || item.packageDetails).map(([category, items]) => {
                              if (category === 'extras' || !items || items.length === 0) return null;

                              const labelMap = {
                                starters: 'Starters',
                                main: 'Main Course',
                                maincourse: 'Main Course',
                                rice: 'Rice',
                                breads: 'Breads',
                                dessert: 'Dessert',
                                desserts: 'Dessert'
                              };
                              const label = labelMap[category] || (category.charAt(0).toUpperCase() + category.slice(1));
                              const count = items.length;

                              return (
                                <Typography
                                  key={category}
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', display: 'block' }}
                                >
                                  • {count} {label}: {items.join(', ')}
                                </Typography>
                              );
                            })}
                            {item.packageDetails.extras && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                • Includes: {item.packageDetails.extras.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Edit link below package summary */}
                        <Typography 
                          variant="caption" 
                          onClick={() => {
                            // Close the cart and navigate to menu with package data for editing
                            onClose();
                            const pkgType = item.packageType === 'premium' ? 'premium' : 'standard';
                            // Store package data in sessionStorage for editing
                            sessionStorage.setItem('editingPackage', JSON.stringify({
                              packageType: pkgType,
                              selectedItems: item.selectedItems,
                              packageDetails: item.packageDetails,
                              guestCount: item.guestCount || item.quantity,
                              fullItem: item
                            }));
                            navigate(`/menu?editVegPackage=${pkgType}`);
                          }}
                          sx={{ 
                            color: '#ff6b35', 
                            fontSize: '0.75rem', 
                            cursor: 'pointer',
                            mt: 0.75,
                            display: 'inline-flex',
                            alignItems: 'center',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          Edit ▶
                        </Typography>
                        {renderDeleteAction(item, 0.75)}
                      </Box>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Serves: {displayServes} | {(item.packageKey || item.packageIncludes) ? 'Select Items' : 'Quantity'}: {quantityText}
                        </Typography>

                        {(item.packageKey || item.packageIncludes) && (
                          <Box sx={{ mt: 0.5 }}>
                            {/* Show selected items if available */}
                            {(item.packageQuantityDetails || item.customizations?.selectedItems) && (
                              <Box sx={{ ml: 0.5 }}>
                                {Object.entries(item.packageQuantityDetails || item.customizations.selectedItems).map(([category, selectedNames]) => {
                                  if (!selectedNames || selectedNames.length === 0) return null;
                                  const labelMap = {
                                    starters: 'Starters',
                                    maincourse: 'Main Course',
                                    rice: 'Rice',
                                    breads: 'Breads',
                                    desserts: 'Desserts'
                                  };
                                  const label = labelMap[category] || category;
                                  return (
                                    <Typography
                                      key={category}
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3 }}
                                    >
                                      • {label}: {selectedNames.join(', ')}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            )}
                            {/* Show limits if no selections yet */}
                            {!(item.packageQuantityDetails || item.customizations?.selectedItems) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem', display: 'block' }}
                              >
                                {(() => {
                                  const toTitleCase = (value) => {
                                    const str = String(value || '').trim();
                                    if (!str) return str;
                                    return str.charAt(0).toUpperCase() + str.slice(1);
                                  };
                                  const includes = item.packageIncludes;
                                  if (!includes || typeof includes !== 'object') return '';
                                  return Object.entries(includes)
                                    .map(([k, v]) => `${toTitleCase(k)}: ${v === true ? 'Yes' : v}`)
                                    .join(' | ');
                                })()}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          {/* Edit link for Jain/Customized items (non-package) */}
                          {!item.isAddon && !(item.packageKey || item.packageIncludes) && (
                          <Typography
                            variant="caption"
                            onClick={() => {
                              // Close cart and go back to menu so user can modify selections
                              onClose();
                              navigate('/menu');
                            }}
                            sx={{
                              color: '#ff6b35',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              mt: 0,
                              display: 'inline-flex',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            Edit ▶
                          </Typography>
                          )}
                          {renderDeleteAction(item, 0)}
                        </Box>
                      </Box>
                    )}

                  </Box>

                  {/* Right Side - Quantity Controls and Price */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Quantity Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e0e0e0', borderRadius: 1, px: 1, py: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const base = parseInt(String(displayServes).replace(/\D/g, ''), 10) || 0;
                          handleQuantityChange(item.id, Math.max(0, base - 1));
                        }}
                        sx={{ 
                          width: 20,
                          height: 20,
                          p: 0
                        }}
                      >
                        <Remove sx={{ fontSize: 12 }} />
                      </IconButton>
                      <Typography sx={{ 
                        minWidth: '20px', 
                        textAlign: 'center', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        mx: 1
                      }}>
                        {displayServes}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const base = parseInt(String(displayServes).replace(/\D/g, ''), 10) || 0;
                          handleQuantityChange(item.id, base + 1);
                        }}
                        sx={{ 
                          width: 20,
                          height: 20,
                          p: 0
                        }}
                      >
                        <Add sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                    
                    {/* Price */}
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1a237e', minWidth: 40 }}>
                      ₹{item._lineTotal}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Divider between items (except last item) */}
                {index < items.length - 1 && (
                  <Box sx={{ borderBottom: '1px solid #f0f0f0', mx: 2 }} />
                )}
              </Box>
            )})}
             {/* Add More Items action - only for Customized and Jain menus */}
        {(selectedMenu === 'customized' || selectedMenu === 'jain') && (
          <Box sx={{ px: 2, pb: 0 }}>
            <Typography
              variant="body2"
              onClick={() => {
                onClose();
                navigate('/menu');
              }}
              sx={{
                color: '#ff6b35',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                mb: 1,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              + Add More Items
            </Typography>
          </Box>
        )}
            {/* Empty state when no items */}
            {mappedItems.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Your cart is empty
                </Typography>
              </Box>
            )}
        </Box>
        </Box>

        {/* Recommended Add ons */}
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Recommended Add-ons
          </Typography>
          {recommendedAddOns.map((addon) => (
            <Box key={addon.id} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              bgcolor: 'white',
              p: 2,
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <Box
                component="img"
                src={addon.image}
                alt={addon.name}
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 1,
                  objectFit: 'cover',
                  mr: 2
                }}
              />
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.2 }}>
                  {addon.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.2, lineHeight: 1 }}>
                  {addon.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: '#132561ff', color: 'white', borderRadius: 0.5, px: 0.5, py: 0.2, mt: 2, mb: 0.2, fontSize: '0.65rem', width: 'fit-content'}}>
                  {addon.rating}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                  ₹{addon.price}
                </Typography>
              </Box>
              
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleAddAddonToCart(addon)}
                sx={{ 
                  minWidth: 60,
                  fontSize: '0.75rem',
                  borderColor: '#1a237e',
                  color: '#1a237e'
                }}
              >
                Add
              </Button>
            </Box>
          ))}
        </Box>

        {/* Total Section */}
        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Total
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Sub Total</Typography>
            <Typography variant="body2">₹{getTotalPrice()}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Delivery Charges</Typography>
            <Typography variant="body2">{DELIVERY_CHARGE_LABEL}</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">GST (Including CGST)</Typography>
            <Typography variant="body2">₹{Math.round(getTotalPrice() * GST_RATE)}</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          {/* Final Total */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              ₹{getTotalPrice() + Math.round(getTotalPrice() * GST_RATE)}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          
          {/* Delivery Date/Time */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Delivery Date</InputLabel>
              <Select
                value={deliveryDate}
                onChange={(e) => handleDeliveryDateChange(e.target.value)}
                label="Delivery Date"
              >
                {/* Always include the current selected date as an option */}
                <MenuItem value={deliveryDate}>{deliveryDate}</MenuItem>
                {deliveryDate !== "15th October 2024" && (
                  <MenuItem value="15th October 2024">15th October 2024</MenuItem>
                )}
                {deliveryDate !== "16th October 2024" && (
                  <MenuItem value="16th October 2024">16th October 2024</MenuItem>
                )}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Delivery Time</InputLabel>
              <Select
                value={deliveryTime}
                onChange={(e) => handleDeliveryTimeChange(e.target.value)}
                label="Delivery Time"
              >
                {/* Always include the current selected time as an option */}
                <MenuItem value={deliveryTime}>{deliveryTime}</MenuItem>
                {deliveryTime !== "6:00 PM - 10:30 PM" && (
                  <MenuItem value="6:00 PM - 10:30 PM">6:00 PM - 10:30 PM</MenuItem>
                )}
                {deliveryTime !== "12:00 PM - 4:00 PM" && (
                  <MenuItem value="12:00 PM - 4:00 PM">12:00 PM - 4:00 PM</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
          
          {/* Location and Phone Number */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                label="Location"
              >
                {locationService.getAvailableLocations().map((loc) => (
                  <MenuItem key={loc.id} value={loc.name}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
          
            <Box sx={{ flex: 1 }}>
              <TextField
                size="small"
                placeholder="Enter Pin Code"
                value={couponCode}
                onChange={handlePinCodeChange}
                error={!!pinCodeError}
                sx={{ width: '100%' }}
                label="Enter Pin Code"
              />
              {pinCodeError && (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                  {pinCodeError}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Phone Number */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                size="small"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                error={!!phoneError}
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]*'
                }}
                sx={{ width: '100%' }}
                label="Phone Number"
              />
              {phoneError && (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                  {phoneError}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleSendOTP}
              sx={{ 
                bgcolor: '#1a237e',
                minWidth: 80,
                '&:hover': { bgcolor: '#303f9f' },
                height: '40px'
              }}
            >
              Send OTP
            </Button>
          </Box>
          
          {/* Inline OTP Verification Section - Like 2nd Image */}
          {showOTP && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, textAlign: 'center' }}>
                A verification code has been sent to {phoneNumber}
              </Typography>

              {otpMessage && (
                <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center', mb: 1, color: '#2e7d32' }}>
                  {otpMessage}
                </Typography>
              )}
              
              {/* 6 OTP Input Fields - Inline like 2nd image */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'center' }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`otp-${index}`}
                    size="small"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 'bold' }
                    }}
                    sx={{ 
                      width: 40, 
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: otpError ? '#f44336' : '#e0e0e0',
                        },
                      }
                    }}
                  />
                ))}
              </Box>
              
              {/* Resend Code - Like 2nd image */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                {canResend ? (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleResendOTP}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', color: '#ff6b35' }}
                  >
                    Resend Code
                  </Button>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
                    Resend code in {resendTimer}s
                  </Typography>
                )}
              </Box>
              
              {/* OTP Error Message */}
              {otpError && (
                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem', display: 'block', textAlign: 'center', mb: 1 }}>
                  {otpError}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Checkout Button */}
          {jainSelectionError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
              {jainSelectionError}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{
              bgcolor: '#1a237e',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#303f9f'
              }
            }}
          >
            Submit
          </Button>
        </Box>
        
        {/* reCAPTCHA container (only needed for Firebase OTP) */}
        {/* <div id="recaptcha-container"></div> */}
      </DialogContent>
      
      
      {/* Order Success Modal */}
      <OrderSuccessModal
        open={showSuccess}
        onClose={handleSuccessClose}
        onOkay={handleSuccessOkay}
      />
    </Dialog>
  );
};

export default memo(CartModal);
